/**
 * Campaign Discovery App
 * Entry point and initialization
 */

import { DiscoveryController } from './controllers/DiscoveryController.js';
import { Modal } from './components/Modal.js';
import { inferFromProductData, inferFromBusinessModel } from './components/promotionInference.js';
import { DiscoveryForm } from './components/DiscoveryForm.js?v=1768137795';
import { DiscoveryRepository } from './repositories/DiscoveryRepository.js';
import { IncompleteCampaignsModal } from './components/IncompleteCampaignsModal.js';
// No query suffix: esbuild treats './config.js?v=...' and './config.js' as two
// separate modules, bundling TWO NexusConfig instances with divergent auth state.
import { NexusConfig } from './config.js';

/**
 * Module-scoped application state
 * Replaces global window.* mutations
 */
const appState = {
    discoveryContext: null,
    version: '2.0.0',
    mode: 'production',
    isNexusMode: false,
    nexusId: null  // PostgreSQL campaign UUID from NEXUS (for linking)
};

/**
 * Get auth headers from the best available source
 * Priority: 1. NEXUS sessionStorage token, 2. AuthService token
 * @returns {Object} Headers object with Authorization if token available
 */
function getBestAuthHeaders() {
    // First: Check for NEXUS JWT in sessionStorage (set by NexusConfig._handleAuthToken)
    const nexusToken = sessionStorage.getItem('nexus_access_token');
    if (nexusToken) {
        console.log('[Auth] Using NEXUS token from sessionStorage');
        return { Authorization: `Bearer ${nexusToken}` };
    }

    // Second: Use AuthService if available
    if (typeof window !== 'undefined' && window.AuthService) {
        const headers = window.AuthService.getAuthHeaders();
        if (headers.Authorization) {
            console.log('[Auth] Using AuthService token');
            return headers;
        }
    }

    // No auth available - return empty headers, let server respond with 401
    console.warn('[Auth] No auth token available - request will be unauthenticated');
    return {};
}

/**
 * Authenticated fetch with one re-auth retry on 401 (expired/invalid token).
 * Rebuilds headers on every attempt so the retry after a REQUEST_AUTH refresh
 * picks up the fresh token from sessionStorage.
 * @param {string} url - Request URL
 * @param {Object} options - fetch options (auth headers merged in fresh)
 * @returns {Promise<Response>}
 */
function fetchWithAuth(url, options = {}) {
    return NexusConfig.fetchWithAuthRetry(() => fetch(url, {
        ...options,
        headers: { ...(options.headers || {}), ...getBestAuthHeaders() }
    }));
}

/**
 * Wait for AUTH_TOKEN from NEXUS parent window
 * Returns a Promise that resolves when token is received or times out
 * @param {number} timeoutMs - Maximum time to wait (default 3000ms)
 * @returns {Promise<boolean>} - True if token received, false if timeout
 */
function waitForAuthToken(timeoutMs = 3000) {
    return new Promise((resolve, reject) => {
        // Check if already authenticated
        if (NexusConfig.nexusUser) {
            console.log('🔐 AUTH_TOKEN already received');
            resolve(true);
            return;
        }

        let resolved = false;

        // Set up callback for when AUTH_TOKEN arrives
        NexusConfig.onAuthReady(() => {
            if (!resolved) {
                resolved = true;
                console.log('🔐 AUTH_TOKEN received - proceeding');
                resolve(true);
            }
        });

        // Timeout - reject with error instead of proceeding without token
        setTimeout(() => {
            if (!resolved) {
                resolved = true;
                console.error('[AUTH ERROR] AUTH_TOKEN timeout - no token received within', timeoutMs, 'ms');
                reject(new Error('AUTH_TOKEN_TIMEOUT: No authentication token received from NEXUS within ' + timeoutMs + 'ms'));
            }
        }, timeoutMs);
    });
}

/**
 * Theme Manager
 * Handles dark/light mode toggle
 */
class ThemeManager {
    constructor() {
        this.html = document.documentElement;
        this.toggle = document.getElementById('themeToggle');
        this.iconLight = this.toggle.querySelector('.icon-light');
        this.iconDark = this.toggle.querySelector('.icon-dark');
    }

    init() {
        // Load saved theme or default to 'dark' (iframe context may have empty localStorage)
        const savedTheme = localStorage.getItem('bizdmp-theme-preference') || 'dark';
        this.setTheme(savedTheme);

        // Setup toggle
        this.toggle.addEventListener('click', () => {
            const currentTheme = this.html.getAttribute('data-theme');
            if (!currentTheme) {
                throw new Error('THEME_NOT_SET: data-theme attribute not set on HTML element');
            }
            const newTheme = currentTheme === 'light' ? 'dark' : 'light';
            this.setTheme(newTheme);
        });
    }

    setTheme(theme) {
        this.html.setAttribute('data-theme', theme);
        localStorage.setItem('bizdmp-theme-preference', theme);

        // Update icon
        if (theme === 'dark') {
            this.iconLight.style.display = 'none';
            this.iconDark.style.display = 'inline';
        } else {
            this.iconLight.style.display = 'inline';
            this.iconDark.style.display = 'none';
        }
    }
}

/**
 * Initialize application
 */
async function init() {
    console.time('[DISCOVERY TIMING] init total');
    console.log('[DISCOVERY TIMING] init started at:', performance.now().toFixed(2), 'ms');
    console.log('🚀 Campaign Discovery AI - Initializing...');

    // Initialize theme manager
    const themeManager = new ThemeManager();
    themeManager.init();

    // Initialize NEXUS mode (detect from URL param and persist to sessionStorage)
    appState.isNexusMode = NexusConfig.init();
    console.log('🔗 NEXUS mode check:', appState.isNexusMode, '| Embedded:', NexusConfig.isEmbedded());

    // UNIFIED IDENTITY (MP-05): Send READY immediately, receive AUTH_TOKEN after
    // AUTH_TOKEN is an enhancement, not a prerequisite
    if (NexusConfig.isEmbedded()) {
        // Add embedded class to body for CSS hiding of header chrome
        document.body.classList.add('embedded');
        console.log('🔗 Added body.embedded class for header hiding');

        // Always send READY immediately for backward compatibility
        console.log('🔗 Running in iframe - sending READY to parent');
        NexusConfig.sendToParent('READY', { system: 'campaigns' });

        // AUTH_TOKEN will be received after READY if NEXUS supports unified identity
        // The _handleAuthToken listener (set up in NexusConfig.init) will store the context
    }

    if (appState.isNexusMode) {
        console.log('🔗 NEXUS mode enabled - full integration active');
    }

    // Standalone mode: embedded via Quick Access, hides profile
    if (NexusConfig.isStandaloneMode()) {
        document.body.classList.add('standalone');
        console.log('🔗 Standalone mode enabled - profile hidden');
    }

    // Hide admin-only project buttons if user is not admin
    // SKIA and Assessment API are admin-only products
    const adminOnlyProjects = ['skia-image-mcp', 'assessment-api'];
    const authServiceExists = typeof AuthService !== 'undefined';
    if (!authServiceExists) {
        console.error('[APP ERROR] AuthService not available');
        throw new Error('AUTH_SERVICE_MISSING: AuthService is required but not loaded');
    }
    const user = AuthService.getUser();
    const isAdmin = authServiceExists && AuthService.isAdmin();

    // Debug logging for admin check
    console.log('🔐 Auth check:', {
        authServiceExists,
        user: user ? { email: user.email, role: user.role } : null,
        isAdmin
    });

    if (!isAdmin) {
        adminOnlyProjects.forEach(projectId => {
            const btn = document.querySelector(`.project-btn[data-project="${projectId}"]`);
            if (btn) {
                btn.style.display = 'none';
            }
        });
        console.log('👤 Non-admin user - admin-only products hidden');
    } else {
        console.log('👑 Admin user - all products visible');
    }

    // Check for edit mode first (highest priority)
    // Supports both ?edit={campaignId} and ?campaignId={id}&mode=edit
    const urlParams = new URLSearchParams(window.location.search);

    // Extract nexusId from URL params (PostgreSQL campaign UUID for linking)
    // NEXUS passes this when embedding discovery in iframe: ?nexusId=<uuid>
    appState.nexusId = urlParams.get('nexusId') || null;
    if (appState.nexusId) {
        console.log(`🔗 NEXUS campaign linking enabled: ${appState.nexusId}`);
    }

    // Campaign name will be fetched from server via token exchange (not URL params)

    const editCampaignId = urlParams.get('edit') ||
        (urlParams.get('mode') === 'edit' ? urlParams.get('campaignId') : null);

    if (editCampaignId) {
        // Edit mode - load existing campaign data and show form
        console.log(`📝 Edit mode: Loading campaign ${editCampaignId}`);

        // Wait for AUTH_TOKEN when embedded (cross-origin requires postMessage auth)
        if (NexusConfig.isEmbedded()) {
            console.log('🔐 Embedded mode - waiting for AUTH_TOKEN...');
            console.time('[DISCOVERY TIMING] waitForAuthToken');
            await waitForAuthToken(3000);
            console.timeEnd('[DISCOVERY TIMING] waitForAuthToken');
        }

        console.time('[DISCOVERY TIMING] loadEditMode');
        await loadEditMode(editCampaignId);
        console.timeEnd('[DISCOVERY TIMING] loadEditMode');
        console.timeEnd('[DISCOVERY TIMING] init total');
        console.log('[DISCOVERY TIMING] init completed at:', performance.now().toFixed(2), 'ms');
        console.log('✓ Application initialized (edit mode)');
        return;
    }

    // Check for copy mode (load data but create new campaign)
    const copyCampaignId = urlParams.get('copy');
    if (copyCampaignId) {
        console.log(`📋 Copy mode: Loading campaign ${copyCampaignId} as template`);

        // Wait for AUTH_TOKEN when embedded
        if (NexusConfig.isEmbedded()) {
            console.log('🔐 Embedded mode - waiting for AUTH_TOKEN...');
            await waitForAuthToken(3000);
        }

        await loadCopyMode(copyCampaignId);
        console.log('✓ Application initialized (copy mode)');
        return;
    }

    // Check for view mode (read-only)
    const viewCampaignId = urlParams.get('view');
    if (viewCampaignId) {
        console.log(`👁️ View mode: Loading campaign ${viewCampaignId} (read-only)`);

        // Wait for AUTH_TOKEN when embedded
        if (NexusConfig.isEmbedded()) {
            console.log('🔐 Embedded mode - waiting for AUTH_TOKEN...');
            await waitForAuthToken(3000);
        }

        // Smart routing: if frameworks already selected, redirect to dashboard
        // Same logic as edit mode — don't show discovery when campaign has progressed past it
        const forceView = urlParams.get('forceView') === 'true';
        if (!forceView) {
            try {
                const dashboardApi = window.CAMPAIGNS_CONFIG.DASHBOARD_API_URL;
                const sessionResponse = await fetchWithAuth(`${dashboardApi}/api/framework/${viewCampaignId}/session`, {
                    headers: getBestAuthHeaders()
                });

                if (sessionResponse.ok) {
                    const sessionData = await sessionResponse.json();
                    if (sessionData.session?.selectedFrameworks?.length > 0) {
                        console.log('🚀 Frameworks already selected - redirecting to dashboard (from view mode)');
                        NexusConfig.navigateTo('/tools/dashboard/web/multi-framework-dashboard.html', { campaignId: viewCampaignId });
                        return;
                    }
                }
            } catch (sessionError) {
                console.error('[APP ERROR] View mode session check failed:', sessionError);
                throw new Error('SESSION_CHECK_FAILED: Cannot verify campaign state - ' + sessionError.message);
            }
        } else {
            console.log('👁️ forceView=true - skipping dashboard redirect, showing view form');
        }

        const isLocked = urlParams.get('locked') === 'true';
        await loadViewMode(viewCampaignId, isLocked);
        console.log('✓ Application initialized (view mode' + (isLocked ? ', locked by cascade' : '') + ')');
        return;
    }

    // Server-side token exchange for accurate role detection
    // The opaque JWT has no user claims — must call /api/auth/exchange to get role
    let serverVerifiedAdmin = isAdmin; // fallback to localStorage check
    appState.campaignName = null;

    if (NexusConfig.isEmbedded()) {
        // Hide welcome card immediately in embedded mode to prevent flash
        // It will be shown only if token exchange confirms admin role
        const welcomeCard = document.getElementById('welcomeCard');
        if (welcomeCard) {
            welcomeCard.style.display = 'none';
        }

        console.log('🔐 Embedded mode - waiting for AUTH_TOKEN...');
        try {
            await waitForAuthToken(3000);

            // Exchange opaque token for full user profile (role, email, etc.)
            // Gateway handles /api/auth/* and /api/campaigns/* (different from discovery backend)
            const gatewayBase = window.CAMPAIGNS_CONFIG.GATEWAY_URL;
            const exchangeUrl = `${gatewayBase}/api/auth/exchange`;
            const exchangeResponse = await fetchWithAuth(exchangeUrl, {
                method: 'POST',
                headers: { ...getBestAuthHeaders(), 'Content-Type': 'application/json' }
            });

            if (exchangeResponse.ok) {
                const exchangeData = await exchangeResponse.json();
                if (exchangeData.success && exchangeData.user) {
                    serverVerifiedAdmin = exchangeData.user.role === 'admin';
                    // Update AuthService with server-verified user data
                    AuthService.setUser(exchangeData.user);
                    console.log('🔐 Token exchange: role =', exchangeData.user.role, '| isAdmin =', serverVerifiedAdmin);

                    // Re-show admin-only product buttons if server confirms admin
                    // (they were hidden at startup before the token arrived)
                    if (serverVerifiedAdmin) {
                        adminOnlyProjects.forEach(projectId => {
                            const btn = document.querySelector(`.project-btn[data-project="${projectId}"]`);
                            if (btn) {
                                btn.style.display = '';
                            }
                        });
                        console.log('👑 Token exchange confirmed admin - product buttons restored');
                    }
                }
            } else {
                console.error('🔐 Token exchange failed:', exchangeResponse.status);
                Modal.toast('Authentication check failed — using default permissions.', 'warning');
            }

            // Get the campaign name from server (needed by both admin and non-admin paths)
            // Admin: pre-fills Custom Product modal so they don't retype the project name
            // Non-admin: pre-fills the discovery form's product name field
            if (appState.nexusId) {
                try {
                    const campaignUrl = `${gatewayBase}/api/campaigns/${appState.nexusId}`;
                    const campaignResponse = await fetchWithAuth(campaignUrl, {
                        headers: getBestAuthHeaders()
                    });
                    if (campaignResponse.ok) {
                        const campaignData = await campaignResponse.json();
                        if (campaignData.success && campaignData.campaign) {
                            appState.campaignName = campaignData.campaign.name || null;
                            console.log('📝 Campaign name from server:', appState.campaignName);
                        }
                    } else {
                        console.warn('📝 Campaign lookup failed:', campaignResponse.status);
                        Modal.toast('Could not load project name — you can enter it manually.', 'warning');
                    }
                } catch (campaignError) {
                    console.error('📝 Campaign lookup error:', campaignError.message);
                    Modal.toast('Could not load project name — you can enter it manually.', 'warning');
                }
            }

        } catch (authError) {
            // Token timeout — fall back to non-admin behavior with visible warning
            console.error('🔐 AUTH_TOKEN timeout:', authError.message);
            Modal.toast('Authentication timed out — showing default view.', 'warning');
            serverVerifiedAdmin = false;
        }
    }

    // Phase 0: Check for localStorage migration
    await checkForLocalStorageMigration();

    // Admin users see the welcome card (product/website selection)
    // Non-admin users skip straight to the discovery form
    const shouldShowWelcome = serverVerifiedAdmin;

    // New campaign mode
    const controller = new DiscoveryController();
    await controller.init();

    if (shouldShowWelcome) {
        // Admin: show welcome card with product selection buttons
        // Re-show welcome card (hidden at startup in embedded mode to prevent flash)
        const welcomeCard = document.getElementById('welcomeCard');
        if (welcomeCard) {
            welcomeCard.style.display = '';
        }
        // Pass campaign name so Custom Product modal can pre-fill it
        setupWelcomeButtons(controller, false, appState.campaignName);
    } else {
        // Non-admin: skip welcome, emulate standalone Custom Product flow
        setupWelcomeButtons(controller, true, appState.campaignName);
    }

    console.log('✓ Application initialized');
}

/**
 * Check for existing localStorage draft and offer migration to server
 */
async function checkForLocalStorageMigration() {
    const localDraft = localStorage.getItem('discoveryFormDraft');
    if (!localDraft) return;

    try {
        const draftData = JSON.parse(localDraft);

        // Only offer migration if brandName exists (required for server save)
        if (!draftData.brandName || !draftData.brandName.trim()) {
            console.log('📝 Local draft found but no brandName - keeping in localStorage');
            return;
        }

        // Check if auth is available before offering cloud migration
        const hasAuth = sessionStorage.getItem('nexus_access_token')
            || (window.AuthService && window.AuthService.isAuthenticated());

        if (!hasAuth) {
            console.log('📝 Local draft found but no auth available - skipping cloud migration prompt');
            return;
        }

        const migrate = await Modal.confirmModal(
            'Save Your Draft to Cloud?',
            'Found a saved draft on this device. Save to server for cross-device access?'
        );

        if (migrate) {
            const apiBase = getAPIBase();
            const repository = new DiscoveryRepository(apiBase);
            const result = await repository.saveCampaignDraft(draftData, appState.nexusId);

            localStorage.removeItem('discoveryFormDraft');
            Modal.toast(`Draft saved to cloud! (${result.completionPercent}% complete)`, 'success');
            console.log('✓ Local draft migrated to server');
        }

    } catch (error) {
        console.error('[APP ERROR] Migration failed:', error);
        Modal.toast('Could not save draft to cloud — keeping local copy.', 'warning');
    }
}

/**
 * Check for incomplete campaigns and show modal if any exist
 * @returns {boolean} True if should show welcome card, false if user resumed a campaign
 */
async function checkForIncompleteCampaigns() {
    const apiBase = getAPIBase();
    const repository = new DiscoveryRepository(apiBase);

    try {
        const incompleteCampaigns = await repository.getIncompleteCampaigns();

        // Check if snoozed
        const snoozeUntil = localStorage.getItem('incompleteCampaignsSnooze');
        const isSnoozed = snoozeUntil && new Date(snoozeUntil) > new Date();

        if (incompleteCampaigns.totalCount > 0 && !isSnoozed) {
            console.log(`📋 Found ${incompleteCampaigns.totalCount} incomplete campaign(s)`);

            const result = await IncompleteCampaignsModal.show(incompleteCampaigns, apiBase);

            if (result.action === 'resume') {
                // Load campaign for editing
                console.log(`📝 Resuming campaign: ${result.campaignId}`);
                await loadEditMode(result.campaignId);
                return false; // Don't show welcome card

            } else if (result.action === 'snooze') {
                console.log('⏰ Snoozed incomplete campaigns for 24 hours');
            }
            // If 'new' action, continue to welcome card
        }

    } catch (error) {
        console.error('[APP ERROR] Failed to check incomplete campaigns:', error);
        throw new Error('INCOMPLETE_CHECK_FAILED: ' + error.message);
    }
}

/**
 * Map old discovery.json schema to new DiscoveryForm schema
 * Handles backward compatibility with campaign data from chat-based flow
 */
export function mapDiscoveryToFormSchema(oldData) {
    // Helper: Infer promotion type from business model
    function inferPromotionType(businessModel) {
        if (!businessModel) return '';
        // MP-07 (review): inference lands ONLY on the five live keys. Retired values are for rows
        // that already carry them (decision 19), never for anything newly generated.
        return inferFromBusinessModel(businessModel);
    }

    // Helper: Infer price point from priceLevel
    function inferPricePoint(priceLevel) {
        if (!priceLevel) return '';
        const pl = priceLevel.toLowerCase();
        if (pl === 'free') return '0';
        if (pl === 'low') return '50';
        if (pl === 'mid') return '500';
        if (pl === 'high') return '5000';
        return '';
    }

    // Helper: Infer purchase type from business model
    function inferPurchaseType(businessModel, priceLevel) {
        if (!businessModel) return '';
        const bm = businessModel.toLowerCase();
        if (bm.includes('subscription') || bm.includes('recurring')) return 'subscription';
        if (bm.includes('service') || bm.includes('consulting') || priceLevel === 'high') return 'contract';
        return 'one-time';
    }

    return {
        // Section 1: Product Info
        // Check new schema fields first, then fall back to old schema inference
        promotionType: oldData.promotionType || inferPromotionType(oldData.businessModel),
        productName: oldData.productName || oldData.brandName || oldData.projectTitle || oldData.project || '',
        websiteUrl: oldData.websiteUrl || '',
        linkedinUrl: oldData.linkedinUrl || '',
        productCategory: oldData.productCategory || oldData.selling || '',
        pricePoint: oldData.pricePoint || inferPricePoint(oldData.priceLevel),
        purchaseType: oldData.purchaseType || inferPurchaseType(oldData.businessModel, oldData.priceLevel),

        // Section 2: Audience
        targetBuyer: oldData.targetBuyer || oldData.buyer || oldData.targetAudience || '',
        awarenessLevel: oldData.awarenessLevel || '',
        buyerPsychology: oldData.buyerPsychology || 'analytical',
        purchaseTimeline: oldData.purchaseTimeline || 'medium',
        triggerEvents: oldData.triggerEvents || '',
        failedApproaches: oldData.failedApproaches || '',

        // Section 3: Content
        // Filter out empty strings from arrays
        painPoints: Array.isArray(oldData.painPoints)
            ? oldData.painPoints.filter(p => p && p.trim()).slice(0, 3)
            : ['', '', ''],
        keyBenefits: Array.isArray(oldData.keyBenefits)
            ? oldData.keyBenefits.filter(b => b && b.trim()).slice(0, 3)
            : ['', '', ''],
        uniqueValue: oldData.uniqueValue || oldData.competitiveAdvantage || '',
        enemyName: oldData.enemyName || '',
        prospectLanguage: oldData.prospectLanguage || '',
        proofAssets: Array.isArray(oldData.proofAssets) ? oldData.proofAssets : [],
        socialProof: oldData.socialProof || oldData.proof || '',

        // Section 4: Offer
        // The goal is named here like every other answer. Unnamed, it was dropped on the way in,
        // the radios rendered unpicked, and the next save wrote that blank back over the person's
        // own answer — the goal that never stuck (founder-reported 2026-08-06).
        campaignGoal: oldData.campaignGoal || '',
        offerDetails: oldData.offerDetails || oldData.offer || '',
        callToAction: oldData.callToAction || 'Learn More',
        primaryChannel: oldData.primaryChannel || 'landing-page',
        trafficSource: oldData.trafficSource || '',
        conversionDestination: oldData.conversionDestination || '',

        // NEXUS mode: Cold/Warm content type selection
        contentType: oldData.contentType || '',

        // Section 5: Brand voice (MP-02) — owner mode only, persists across sessions
        brandVoice: oldData.brandVoice || '',
        // Every answer the teaser and the new steps collect. Without these the form loaded them
        // blank and the next save wrote that blank back over the person's own answers — the
        // save-wipes-a-column defect (founder-reported 2026-08-05).
        productDescription: oldData.productDescription || '',
        brandTones: oldData.brandTones || '',
        adIntensity: Number.isInteger(oldData.adIntensity) ? oldData.adIntensity : 3,
        problemCost: oldData.problemCost || '',
        problemCostPeriod: oldData.problemCostPeriod || '',
        proofNumber: oldData.proofNumber || '',
        proofQuoteName: oldData.proofQuoteName || '',
        proofQuoteRole: oldData.proofQuoteRole || '',
        actionUrl: oldData.actionUrl || '',
        offerHasDeal: oldData.offerHasDeal === true ? 'yes' : oldData.offerHasDeal === false ? 'no' : (oldData.offerHasDeal || ''),
        offerNormalPrice: oldData.offerNormalPrice || '',
        offerPrice: oldData.offerPrice || '',
        offerEnds: oldData.offerEnds || '',
        eventName: oldData.eventName || '',
        eventDate: oldData.eventDate || '',
        eventStartTime: oldData.eventStartTime || '',
        eventEndTime: oldData.eventEndTime || '',
        eventFormat: oldData.eventFormat || '',
        eventLocation: oldData.eventLocation || '',
        eventHost: oldData.eventHost || '',
        eventHostCredential: oldData.eventHostCredential || '',
        announcementSubject: oldData.announcementSubject || '',
        announcementWhen: oldData.announcementWhen || '',
        // ads-brief-per-type-cutover step 5: each type's own answers, plus the optional objection.
        // Unnamed here they would be dropped on reload and the next save would write the blank back.
        announcementImpact: oldData.announcementImpact || '',
        courseStarts: oldData.courseStarts || '',
        courseTeacher: oldData.courseTeacher || '',
        serviceArea: oldData.serviceArea || '',
        serviceAvailability: oldData.serviceAvailability || '',
        topObjection: oldData.topObjection || '',

        // Preserve brandName for server saves (backend requires it)
        brandName: oldData.brandName || oldData.productName || '',

        // Preserve campaignId for updates (prevents duplicate campaign creation)
        campaignId: oldData.campaignId || ''
    };
}

/**
 * Load edit mode - fetch campaign data and show form
 */
async function loadEditMode(campaignId) {
    const welcomeCard = document.getElementById('welcomeCard');
    const chatContainer = document.getElementById('chatContainer');
    const formContainer = document.getElementById('discovery-form-container');

    // Declare at function scope so discoveryComplete handler can access it
    let dashboardApiBase;
    // "Back to Dashboard" belongs ONLY to an established campaign — discovery complete AND a framework
    // selected. Default false so a new / incomplete / converted-lead discovery never gets a skip escape.
    let hasSelectedFramework = false;

    // Check for forceEdit flag - if set, skip redirect to dashboard
    const urlParams = new URLSearchParams(window.location.search);
    const forceEdit = urlParams.get('forceEdit') === 'true';
    console.log('📝 Edit mode - forceEdit:', forceEdit);

    // Hide welcome and chat
    welcomeCard.style.display = 'none';
    chatContainer.style.display = 'none';

    // Show form container
    formContainer.style.display = 'block';

    try {
        // Fetch campaign discovery data
        const apiBase = getAPIBase();
        const repository = new DiscoveryRepository(apiBase);
        const campaignData = await repository.getDiscoveryData(campaignId);

        console.log('✅ Campaign data loaded (old schema):', campaignData);
        console.log('📋 [DEBUG] Raw contentType from server:', JSON.stringify(campaignData.contentType), '| type:', typeof campaignData.contentType);

        // Check if frameworks already selected - if so, redirect to dashboard
        // This implements "smart routing" - go to next incomplete step
        try {
            dashboardApiBase = window.CAMPAIGNS_CONFIG.DASHBOARD_API_URL;
            const sessionResponse = await fetchWithAuth(`${dashboardApiBase}/api/framework/${campaignId}/session`, {
                headers: getBestAuthHeaders()
            });

            if (sessionResponse.ok) {
                const sessionData = await sessionResponse.json();
                hasSelectedFramework = sessionData.session?.selectedFrameworks?.length > 0;
                // Only redirect if frameworks exist AND user didn't force edit mode
                if (!forceEdit && hasSelectedFramework) {
                    console.log('🚀 Frameworks already selected - redirecting to dashboard');
                    NexusConfig.navigateTo('/tools/dashboard/web/multi-framework-dashboard.html', { campaignId });
                    return;
                }
                if (forceEdit) {
                    console.log('📝 forceEdit=true - skipping dashboard redirect, showing edit form');
                }
            }
        } catch (sessionError) {
            console.error('[APP ERROR] Session check failed:', sessionError);
            throw new Error('SESSION_CHECK_FAILED: Cannot verify campaign state - ' + sessionError.message);
        }

        // Map old discovery schema to new form schema
        const mappedData = mapDiscoveryToFormSchema(campaignData);

        console.log('✅ Mapped to form schema:', mappedData);

        // editMode (and its "Back to Dashboard" escape) only when a framework is already selected —
        // otherwise the fill-in form shows "Continue to Framework Selection" and cannot be skipped.
        const form = new DiscoveryForm(campaignId, mappedData, { editMode: hasSelectedFramework });
        form.render();

        // Listen for form completion - draft continues to framework selection
        window.addEventListener('discoveryComplete', async (e) => {
            const { formData, campaignId: cid } = e.detail;
            console.log('📝 Edit mode - form completed, saving and proceeding to framework selection');

            try {
                // Save/update to server to ensure campaign.json exists
                // Always update the existing campaign in edit mode
                // Note: "Copy to NEXUS" uses ?copy= mode (separate flow), not edit mode
                formData.campaignId = campaignId;
                const apiBase = getAPIBase();
                const repository = new DiscoveryRepository(apiBase);
                const result = await repository.saveCampaignDraft(formData, appState.nexusId);
                console.log('💾 Campaign draft updated:', result);

                // NEXUS: Send CAMPAIGN_CREATED message
                NexusConfig.sendToParent('CAMPAIGN_CREATED', {
                    campaignId: result.campaignId,
                    brandName: formData.productName || ''
                });

                // Save to localStorage for framework selector
                localStorage.setItem('discoveryFormData', JSON.stringify(formData));
                localStorage.setItem('pendingCampaignId', result.campaignId);

                // Detect if content type changed (Cold↔Warm) — requires framework re-selection
                const originalContentType = campaignData.contentType || '';
                const newContentType = formData.contentType || '';
                const contentTypeChanged = originalContentType && newContentType && originalContentType !== newContentType;

                if (contentTypeChanged) {
                    // Content type changed — clear existing frameworks, content, and session
                    console.log(`📝 Content type changed: ${originalContentType} → ${newContentType} — clearing all generated content`);
                    try {
                        // 1. Fetch existing session to get selected framework IDs
                        const fwResponse = await fetchWithAuth(`${dashboardApiBase}/api/framework/${result.campaignId}/session`, {
                            headers: getBestAuthHeaders()
                        });
                        if (fwResponse.ok) {
                            const fwData = await fwResponse.json();
                            const existingFrameworks = fwData.session?.selectedFrameworks || [];
                            // 2. Delete generated content for each framework
                            for (const fw of existingFrameworks) {
                                const fwId = fw.id || fw;
                                try {
                                    await fetchWithAuth(`${dashboardApiBase}/api/framework/${result.campaignId}/${fwId}`, {
                                        method: 'DELETE',
                                        headers: getBestAuthHeaders()
                                    });
                                    console.log(`🗑️ Deleted generated content for framework: ${fwId}`);
                                } catch (delErr) {
                                    console.error(`[APP] Failed to delete framework ${fwId}:`, delErr);
                                }
                            }
                        }
                        // 3. Clear framework session
                        await fetchWithAuth(`${dashboardApiBase}/api/framework/${result.campaignId}/session`, {
                            method: 'POST',
                            headers: { ...getBestAuthHeaders(), 'Content-Type': 'application/json' },
                            body: JSON.stringify({ selectedFrameworks: [], discoveryData: formData, activeFramework: null })
                        });
                    } catch (clearErr) {
                        console.error('[APP] Framework/content clear failed:', clearErr);
                    }
                    Modal.toast('Content type changed! Please re-select frameworks...', 'success');
                    NexusConfig.navigateTo('/tools/discovery/web/framework-selector.html', {
                        campaignId: result.campaignId
                    });
                } else {
                    // Content type unchanged — check if frameworks exist to decide navigation
                    // If frameworks exist: go to dashboard (normal edit flow)
                    // If frameworks empty: go to framework selector (e.g. after upload cleared them)
                    let hasFrameworks = false;
                    try {
                        const fwResponse = await fetchWithAuth(`${dashboardApiBase}/api/framework/${result.campaignId}/session`, {
                            headers: getBestAuthHeaders()
                        });
                        if (fwResponse.ok) {
                            const fwData = await fwResponse.json();
                            hasFrameworks = fwData.session?.selectedFrameworks?.length > 0;
                        }
                    } catch (fwErr) {
                        console.error('[APP] Framework session check failed:', fwErr);
                    }

                    if (hasFrameworks) {
                        Modal.toast('Changes saved! Returning to dashboard...', 'success');
                        NexusConfig.navigateTo('/tools/dashboard/web/multi-framework-dashboard.html', {
                            campaignId: result.campaignId,
                            edited: 'true'
                        });
                    } else {
                        Modal.toast('Changes saved! Proceeding to framework selection...', 'success');
                        NexusConfig.navigateTo('/tools/discovery/web/framework-selector.html', {
                            campaignId: result.campaignId
                        });
                    }
                }

            } catch (error) {
                console.error('[APP ERROR] Failed to save campaign:', error);
                // The person is about to see an error and the page stays put — tell the form so the
                // submit control comes back. Added 11 August 2026 with the working spinner.
                window.dispatchEvent(new CustomEvent('discoveryHandoffFailed'));
                await Modal.alertModal(
                    '⚠️ Save Error',
                    `Could not save campaign: ${error.message}`
                );
                throw error;  // Propagate so caller knows save failed
            }
        }, { once: true });

    } catch (error) {
        console.error('❌ Failed to load campaign:', error);

        // Check if this is a "discovery not found" error (legacy campaign)
        const isLegacyCampaign = error.message.toLowerCase().includes('not found');

        if (isLegacyCampaign) {
            formContainer.innerHTML = `
                <div style="padding: 2rem; text-align: center; max-width: 500px; margin: 0 auto;">
                    <h2 style="color: var(--color-warning-600); margin-bottom: 1rem;">Legacy Campaign</h2>
                    <p style="color: var(--text-secondary); margin: 1rem 0; line-height: 1.6;">
                        This campaign was created before the Discovery system was implemented,
                        so there's no discovery data to edit.
                    </p>
                    <p style="color: var(--text-tertiary); margin: 1rem 0; font-size: 0.9rem;">
                        Campaign ID: <code style="background: var(--bg-tertiary); padding: 0.2rem 0.5rem; border-radius: 0.25rem;">${campaignId}</code>
                    </p>
                    <div style="display: flex; gap: 1rem; justify-content: center; margin-top: 1.5rem; flex-wrap: wrap;">
                        <button onclick="window.history.back()"
                                style="padding: 0.75rem 1.5rem; background: var(--bg-secondary); color: var(--text-primary); border: 1px solid var(--border-primary); border-radius: 0.375rem; cursor: pointer;">
                            Back to Dashboard
                        </button>
                        <button onclick="window.location.href='/tools/discovery/web/campaign-discovery-chat.html'"
                                style="padding: 0.75rem 1.5rem; background: var(--color-primary-600); color: var(--text-inverse); border: none; border-radius: 0.375rem; cursor: pointer;">
                            Start New Campaign
                        </button>
                    </div>
                </div>
            `;
        } else {
            formContainer.innerHTML = `
                <div style="padding: 2rem; text-align: center;">
                    <h2 style="color: var(--color-error-600);">Failed to Load Campaign</h2>
                    <p style="color: var(--text-secondary); margin: 1rem 0;">${error.message}</p>
                    <button onclick="window.location.href='/tools/discovery/web/campaign-discovery-chat.html'"
                            style="padding: 0.75rem 1.5rem; background: var(--color-primary-600); color: var(--text-inverse); border: none; border-radius: 0.375rem; cursor: pointer;">
                        Start New Campaign
                    </button>
                </div>
            `;
        }
    }
}

/**
 * Load copy mode - fetch campaign data, pre-fill form as new campaign
 */
async function loadCopyMode(sourceCampaignId) {
    const welcomeCard = document.getElementById('welcomeCard');
    const chatContainer = document.getElementById('chatContainer');
    const formContainer = document.getElementById('discovery-form-container');

    // Hide welcome and chat
    welcomeCard.style.display = 'none';
    chatContainer.style.display = 'none';

    // Show form container
    formContainer.style.display = 'block';

    try {
        // Fetch source campaign discovery data
        const apiBase = getAPIBase();
        const repository = new DiscoveryRepository(apiBase);
        const campaignData = await repository.getDiscoveryData(sourceCampaignId);

        console.log('✅ Source campaign data loaded:', campaignData);

        // Map old discovery schema to new form schema
        const mappedData = mapDiscoveryToFormSchema(campaignData);

        // Add "(Copy)" suffix to product name
        if (mappedData.productName) {
            mappedData.productName = `${mappedData.productName} (Copy)`;
        }

        console.log('✅ Mapped data for copy:', mappedData);

        // Initialize form WITHOUT campaignId (null = new campaign)
        const form = new DiscoveryForm(null, mappedData);
        form.render();

        // Listen for form completion - treat as new campaign
        window.addEventListener('discoveryComplete', async (e) => {
            const { formData } = e.detail;
            console.log('📋 Copy mode - form completed, saving and proceeding to framework selection');

            try {
                // Save to server to create campaign.json (new campaign from copy)
                const apiBase = getAPIBase();
                const repository = new DiscoveryRepository(apiBase);
                const result = await repository.saveCampaignDraft(formData, appState.nexusId);
                console.log('💾 Copied campaign saved:', result);

                // NEXUS: Send CAMPAIGN_CREATED message
                NexusConfig.sendToParent('CAMPAIGN_CREATED', {
                    campaignId: result.campaignId,
                    brandName: formData.productName || ''
                });

                // Save to localStorage for framework selector
                localStorage.setItem('discoveryFormData', JSON.stringify(formData));
                localStorage.setItem('pendingCampaignId', result.campaignId);

                Modal.toast('Discovery complete! Proceeding to framework selection...', 'success');

                NexusConfig.navigateTo('/tools/discovery/web/framework-selector.html', { campaignId: result.campaignId });

            } catch (error) {
                console.error('Failed to save copied campaign:', error);
                // The person is about to see an error and the page stays put — tell the form so the
                // submit control comes back. Added 11 August 2026 with the working spinner.
                window.dispatchEvent(new CustomEvent('discoveryHandoffFailed'));
                await Modal.alertModal(
                    '⚠️ Save Error',
                    'Could not save campaign to server. Please try again.'
                );
            }
        }, { once: true });

        // Show info message about copy mode
        const infoDiv = document.createElement('div');
        infoDiv.className = 'copy-mode-notice';
        infoDiv.style.cssText = 'background: var(--color-primary-50); border: 1px solid var(--color-primary-200); border-radius: 0.5rem; padding: 1rem; margin-bottom: 1rem; text-align: center;';
        infoDiv.innerHTML = `<p style="color: var(--color-primary-700); margin: 0;">📋 Creating a new campaign based on <strong>${campaignData.productName || sourceCampaignId}</strong></p>`;

        const formElement = formContainer.querySelector('form');
        if (formElement) {
            formElement.insertBefore(infoDiv, formElement.firstChild);
        }

    } catch (error) {
        console.error('❌ Failed to load source campaign:', error);
        formContainer.innerHTML = `
            <div style="padding: 2rem; text-align: center;">
                <h2 style="color: var(--color-error-600);">Failed to Copy Campaign</h2>
                <p style="color: var(--text-secondary); margin: 1rem 0;">${error.message}</p>
                <button onclick="window.location.href='/tools/discovery/web/campaign-discovery-chat.html'"
                        style="padding: 0.75rem 1.5rem; background: var(--color-primary-600); color: var(--text-inverse); border: none; border-radius: 0.375rem; cursor: pointer;">
                    Start Fresh Campaign
                </button>
            </div>
        `;
    }
}

/**
 * Load view mode - fetch campaign data and show in read-only form
 */
async function loadViewMode(campaignId, locked = false) {
    const welcomeCard = document.getElementById('welcomeCard');
    const chatContainer = document.getElementById('chatContainer');
    const formContainer = document.getElementById('discovery-form-container');

    // Hide welcome and chat
    welcomeCard.style.display = 'none';
    chatContainer.style.display = 'none';

    // Show form container
    formContainer.style.display = 'block';

    try {
        // Fetch campaign discovery data
        const apiBase = getAPIBase();
        const repository = new DiscoveryRepository(apiBase);
        const campaignData = await repository.getDiscoveryData(campaignId);

        console.log('✅ Campaign data loaded for viewing:', campaignData);

        // Map old discovery schema to new form schema
        const mappedData = mapDiscoveryToFormSchema(campaignData);

        // Initialize form with campaignId and viewOnly flag
        // DiscoveryForm handles: banner, disabled inputs, button text, back navigation
        const form = new DiscoveryForm(campaignId, mappedData, { viewOnly: true, locked });
        form.render();

    } catch (error) {
        console.error('❌ Failed to load campaign for viewing:', error);
        formContainer.innerHTML = `
            <div style="padding: 2rem; text-align: center;">
                <h2 style="color: var(--color-error-600);">Failed to Load Campaign</h2>
                <p style="color: var(--text-secondary); margin: 1rem 0;">${error.message}</p>
                <button onclick="window.history.back()"
                        style="padding: 0.75rem 1.5rem; background: var(--color-primary-600); color: var(--text-inverse); border: none; border-radius: 0.375rem; cursor: pointer;">
                    Go Back
                </button>
            </div>
        `;
    }
}

/**
 * Get API base URL based on environment
 */
function getAPIBase() {
    return window.CAMPAIGNS_CONFIG.DISCOVERY_API_URL;
}

/**
 * Value mapping functions for form pre-fill
 * Convert API data values to form-compatible radio button values
 */

/**
 * Map revenue model to purchase type radio button value
 * Handles both string and object formats: "hybrid" or { type: "SaaS-Usage", ... }
 */
function mapPurchaseType(revenueModel) {
    if (!revenueModel) return '';

    // Handle object format: { type: 'SaaS-Usage', ... }
    let model = '';
    if (typeof revenueModel === 'object') {
        model = (revenueModel.type || revenueModel.description || '').toLowerCase();
    } else {
        model = String(revenueModel).toLowerCase();
    }

    if (model.includes('subscription') || model.includes('saas') || model.includes('recurring') || model.includes('hybrid') || model.includes('usage')) {
        return 'subscription';
    }
    if (model.includes('one-time') || model.includes('perpetual') || model.includes('license')) {
        return 'one-time';
    }
    if (model.includes('service') || model.includes('consulting') || model.includes('retainer') || model.includes('contract')) {
        return 'contract';
    }
    return '';
}

/**
 * Map go-to-market channel to primary channel radio button value
 * Handles both string and object formats
 */
function mapPrimaryChannel(channel) {
    if (!channel) return '';

    // Handle object format if needed
    let ch = '';
    if (typeof channel === 'object') {
        ch = (channel.type || channel.name || channel.channel || '').toLowerCase();
    } else {
        ch = String(channel).toLowerCase();
    }

    if (ch.includes('email')) return 'email';
    if (ch.includes('ads') || ch.includes('paid') || ch.includes('ppc')) return 'ads';
    if (ch.includes('social')) return 'social';
    if (ch.includes('outreach') || ch.includes('cold')) return 'cold-outreach';
    if (ch.includes('video') || ch.includes('vsl')) return 'vsl';
    if (ch.includes('landing') || ch.includes('page') || ch.includes('b2b') || ch.includes('direct')) return 'landing-page';
    return '';
}

/**
 * Infer promotion type from product data
 */
function inferPromotionType(productData) {
    // MP-07 (review): delegated to the one inference source — only live keys come back.
    return inferFromProductData(productData);
}

/**
 * Infer buyer psychology from buyer personas
 */
function inferBuyerPsychology(buyerPersonas) {
    if (!buyerPersonas?.primaryPersona?.psychographics) return '';
    const psycho = buyerPersonas.primaryPersona.psychographics;
    const goals = (psycho.goals || []).join(' ').toLowerCase();
    const motivations = (psycho.motivations || []).join(' ').toLowerCase();
    const challenges = (psycho.challenges || []).join(' ').toLowerCase();
    const combined = goals + ' ' + motivations + ' ' + challenges;

    // Analytical: efficiency, cost, time savings, metrics, ROI focused
    if (combined.includes('data') || combined.includes('metrics') || combined.includes('roi') ||
        combined.includes('proof') || combined.includes('efficien') || combined.includes('cost') ||
        combined.includes('time') || combined.includes('scale') || combined.includes('automat')) {
        return 'analytical';
    }
    // Emotional: transformation, outcomes, visualization
    if (combined.includes('transform') || combined.includes('outcome') || combined.includes('visualiz') ||
        combined.includes('success') || combined.includes('achiev')) {
        return 'emotional';
    }
    // Authority-seeking: credentials, expertise, professional
    if (combined.includes('credential') || combined.includes('authority') || combined.includes('expert') ||
        combined.includes('professional') || combined.includes('quality')) {
        return 'authority-seeking';
    }
    // Risk-averse: guarantees, safety, security
    if (combined.includes('risk') || combined.includes('guarantee') || combined.includes('safe') ||
        combined.includes('secur') || combined.includes('complian')) {
        return 'risk-averse';
    }
    return '';
}

/**
 * Infer purchase timeline from buyer personas
 */
function inferPurchaseTimeline(buyerPersonas) {
    if (!buyerPersonas?.primaryPersona?.buyingBehavior?.timeline) return '';
    const timeline = buyerPersonas.primaryPersona.buyingBehavior.timeline.toLowerCase();

    if (timeline.includes('day') || timeline.includes('immediate') || timeline.includes('hour')) {
        return 'short';
    }
    if (timeline.includes('week') || timeline.includes('2-4') || timeline.includes('1-2')) {
        return 'medium';
    }
    if (timeline.includes('month') || timeline.includes('1-3') || timeline.includes('quarter')) {
        return 'long';
    }
    return '';
}

/**
 * Load product knowledge (product + business analysis)
 */
async function loadProductData(productId) {
    try {
        const apiBase = getAPIBase();
        const repository = new DiscoveryRepository(apiBase);
        // Use product-knowledge endpoint to get both product and business data
        const knowledge = await repository.getProductKnowledge(productId);
        // Return combined data structure
        const result = {
            ...knowledge.product,
            _business: knowledge.business,
            _hasKnowledge: knowledge.hasData
        };
        return result;
    } catch (error) {
        console.warn(`Could not load product knowledge for ${productId}:`, error);
        // Fallback to product-only data
        try {
            const apiBase = getAPIBase();
            const repository = new DiscoveryRepository(apiBase);
            return await repository.getProductData(productId);
        } catch (fallbackError) {
            console.warn(`Could not load product data for ${productId}:`, fallbackError);
            return null;
        }
    }
}

/**
 * Show discovery form with pre-filled product data
 */
async function showDiscoveryFormWithPreFill(productId, productData) {
    const welcomeCard = document.getElementById('welcomeCard');
    const chatContainer = document.getElementById('chatContainer');
    const formContainer = document.getElementById('discovery-form-container');

    // Hide welcome and chat
    welcomeCard.style.display = 'none';
    chatContainer.style.display = 'none';

    // Show form container
    formContainer.style.display = 'block';

    // Check if there's a draft - offer to use new data or resume draft
    const draft = localStorage.getItem('discoveryFormDraft');

    if (draft) {
        const shouldUseNewData = await Modal.confirmModal(
            '📝 Found Previous Session',
            'We have new product information to pre-fill.\n\nClick "Confirm" to use NEW product data\nClick "Cancel" to resume PREVIOUS session'
        );

        if (!shouldUseNewData) {
            // User wants old draft instead of new product data
            const draftData = JSON.parse(draft);
            const form = new DiscoveryForm(null, draftData);
            form.render();

            appState.discoveryContext = {
                productId,
                productData: null
            };
            return;
        } else {
            // User chose new data - clear all stale campaign data
            localStorage.removeItem('discoveryFormDraft');
            localStorage.removeItem('discoveryFormData');
            localStorage.removeItem('pendingCampaignId');
        }
    }

    // Starting a new campaign - clear stale campaign data even if no draft existed
    localStorage.removeItem('discoveryFormData');
    localStorage.removeItem('pendingCampaignId');

    // Initialize form with empty data (no draft)
    const form = new DiscoveryForm();

    if (productData) {
        const business = productData._business || {};
        console.log('[PRE-FILL] business:', business);
        console.log('[PRE-FILL] business.revenueModel:', business.revenueModel, typeof business.revenueModel);

        // === SECTION 1: Product Info ===
        // Promotion type - infer from product characteristics
        form.formData.promotionType = inferPromotionType(productData);
        console.log('[PRE-FILL] promotionType:', form.formData.promotionType);

        form.formData.productName = productData.name || '';
        form.formData.productCategory = productData.technicalSpecs?.apiType || productData.category || '';

        // Price point - try business analysis first, then product
        form.formData.pricePoint = business.pricing?.base
            || productData.offer?.basePrice
            || productData.pricing?.basePrice
            || '';

        // Purchase type - map revenue model to form value
        form.formData.purchaseType = mapPurchaseType(business.revenueModel)
            || mapPurchaseType(productData.businessModel?.type)
            || mapPurchaseType(productData.offer?.type)
            || '';
        console.log('[PRE-FILL] purchaseType:', form.formData.purchaseType);
        console.log('[PRE-FILL] pricePoint:', form.formData.pricePoint);

        // === SECTION 2: Audience ===
        // Target buyer - combine product and business data
        form.formData.targetBuyer = business.targetBuyer?.primary
            || productData.targetAudience
            || '';

        // Awareness level - infer from business targeting or buyer personas
        if (business.targetBuyer?.primary) {
            const targeting = business.targetBuyer.primary.toLowerCase();
            if (targeting.includes('pain') || targeting.includes('problem') || targeting.includes('struggling')) {
                form.formData.awarenessLevel = 'problem-aware';
            } else if (targeting.includes('comparing') || targeting.includes('looking for') || targeting.includes('evaluating')) {
                form.formData.awarenessLevel = 'solution-aware';
            } else {
                form.formData.awarenessLevel = 'solution-aware';
            }
        } else if (productData.buyerPersonas?.primaryPersona?.buyingBehavior?.triggers) {
            // Infer from buying triggers
            const triggers = productData.buyerPersonas.primaryPersona.buyingBehavior.triggers.join(' ').toLowerCase();
            if (triggers.includes('compliance') || triggers.includes('enterprise') || triggers.includes('requirement')) {
                form.formData.awarenessLevel = 'solution-aware';
            } else if (triggers.includes('bottleneck') || triggers.includes('manual') || triggers.includes('problem')) {
                form.formData.awarenessLevel = 'problem-aware';
            } else {
                form.formData.awarenessLevel = 'solution-aware';
            }
        } else if (productData.targetAudience) {
            // Infer from target audience description
            const audience = productData.targetAudience.toLowerCase();
            if (audience.includes('need') || audience.includes('looking') || audience.includes('want')) {
                form.formData.awarenessLevel = 'solution-aware';
            } else {
                form.formData.awarenessLevel = 'solution-aware'; // Safe default for B2B
            }
        }

        // Buyer psychology - map from buyer personas to form radio value
        form.formData.buyerPsychology = inferBuyerPsychology(productData.buyerPersonas);
        console.log('[PRE-FILL] buyerPsychology:', form.formData.buyerPsychology);

        // Purchase timeline - map from buyer personas to form radio value
        form.formData.purchaseTimeline = inferPurchaseTimeline(productData.buyerPersonas);
        console.log('[PRE-FILL] purchaseTimeline:', form.formData.purchaseTimeline);
        console.log('[PRE-FILL] awarenessLevel:', form.formData.awarenessLevel);

        // Trigger events - from buyer persona buying behavior
        if (productData.buyerPersonas?.primaryPersona?.buyingBehavior?.triggers) {
            const triggers = productData.buyerPersonas.primaryPersona.buyingBehavior.triggers;
            form.formData.triggerEvents = Array.isArray(triggers) ? triggers.join(', ') : triggers;
        }
        console.log('[PRE-FILL] triggerEvents:', form.formData.triggerEvents);

        // Failed approaches - from positioning or competitive intel
        if (productData.positioning?.positioningStatement?.notThis) {
            form.formData.failedApproaches = productData.positioning.positioningStatement.notThis;
        } else if (productData.competitiveIntelligence?.analysis?.marketGaps) {
            const gaps = productData.competitiveIntelligence.analysis.marketGaps;
            form.formData.failedApproaches = Array.isArray(gaps) ? gaps.join(', ') : gaps;
        }
        console.log('[PRE-FILL] failedApproaches:', form.formData.failedApproaches);

        // === SECTION 3: Content ===
        // Pain points from business analysis
        if (business.targetBuyer?.painPoints && Array.isArray(business.targetBuyer.painPoints)) {
            form.formData.painPoints = business.targetBuyer.painPoints.slice(0, 3);
            // Pad with empty strings if less than 3
            while (form.formData.painPoints.length < 3) {
                form.formData.painPoints.push('');
            }
        } else if (productData.buyerPersonas?.primaryPersona?.psychographics?.challenges) {
            // Fallback to buyer persona challenges
            const challenges = productData.buyerPersonas.primaryPersona.psychographics.challenges;
            form.formData.painPoints = challenges.slice(0, 3);
            while (form.formData.painPoints.length < 3) {
                form.formData.painPoints.push('');
            }
        }

        // Key benefits from features (first 3)
        if (productData.features && Array.isArray(productData.features)) {
            form.formData.keyBenefits = productData.features.slice(0, 3);
        }

        // Unique value proposition - try business positioning first
        // Handle positioningStatement which can be string or object {notThis, isThis, whyItMatters}
        let uniqueValue = '';
        if (typeof business.positioning === 'string') {
            uniqueValue = business.positioning;
        } else if (productData.positioning?.positioningStatement) {
            const ps = productData.positioning.positioningStatement;
            if (typeof ps === 'string') {
                uniqueValue = ps;
            } else if (typeof ps === 'object' && ps.isThis) {
                // Format: "NOT [notThis], IS [isThis]"
                uniqueValue = ps.notThis
                    ? `NOT ${ps.notThis}, IS ${ps.isThis}`
                    : ps.isThis;
            }
        }
        if (!uniqueValue && productData.competitiveIntelligence?.analysis?.recommendedPositioning) {
            uniqueValue = productData.competitiveIntelligence.analysis.recommendedPositioning;
        }
        if (!uniqueValue) {
            uniqueValue = productData.description || '';
        }
        form.formData.uniqueValue = uniqueValue;

        // Enemy name - from positioning
        form.formData.enemyName = productData.positioning?.enemyName
            || productData.competitiveIntelligence?.analysis?.enemyAngle
            || '';
        console.log('[PRE-FILL] enemyName:', form.formData.enemyName);

        // Prospect language - from buyer persona quotes
        form.formData.prospectLanguage = productData.buyerPersonas?.primaryPersona?.realQuote
            || productData.buyerPersonas?.primaryPersona?.psychographics?.frustratedBy?.join('. ')
            || '';
        console.log('[PRE-FILL] prospectLanguage:', form.formData.prospectLanguage);

        // Social proof - try multiple sources
        if (!form.formData.socialProof) {
            // Try buyer personas array format
            if (productData.buyerPersonas && Array.isArray(productData.buyerPersonas)) {
                const quotes = productData.buyerPersonas
                    .map(p => p.quote || '')
                    .filter(q => q)
                    .join('\n\n');
                if (quotes) form.formData.socialProof = quotes;
            }
            // Try buyer personas object format - extract from primaryPersona
            else if (productData.buyerPersonas?.primaryPersona) {
                const persona = productData.buyerPersonas.primaryPersona;
                // Handle both formats: title/company OR demographics.jobTitle/companySize
                const title = persona.title || persona.demographics?.jobTitle || '';
                const company = persona.company || persona.demographics?.industry || '';
                const goals = (persona.psychographics?.goals || []).slice(0, 2).join(', ');
                if (title && goals) {
                    form.formData.socialProof = `Target: ${title}${company ? ' in ' + company : ''}. Key goals: ${goals}.`;
                }
            }
            // Try competitive intelligence for proof points
            if (!form.formData.socialProof && productData.competitiveIntelligence?.analysis?.recommendedProof) {
                form.formData.socialProof = productData.competitiveIntelligence.analysis.recommendedProof;
            }
            // Try product features as proof summary
            if (!form.formData.socialProof && productData.features && Array.isArray(productData.features)) {
                const proofFeatures = productData.features
                    .filter(f => f.toLowerCase().includes('save') || f.toLowerCase().includes('reduce') || f.toLowerCase().includes('%'))
                    .slice(0, 2);
                if (proofFeatures.length > 0) {
                    form.formData.socialProof = proofFeatures.join('\n');
                }
            }
        }

        // === SECTION 4: Offer ===
        // Offer details - format nicely instead of raw JSON
        if (productData.offer && typeof productData.offer === 'object') {
            const offer = productData.offer;
            let offerText = '';
            if (offer.type) offerText += `Type: ${offer.type}\n`;
            if (offer.basePrice) offerText += `Starting at: $${offer.basePrice}/month\n`;
            if (offer.tiers && Array.isArray(offer.tiers)) {
                offerText += '\nPricing Tiers:\n';
                offer.tiers.forEach(tier => {
                    offerText += `- ${tier.name}: ${tier.price || '$' + tier.price}${tier.limit ? ' (' + tier.limit + ')' : ''}\n`;
                });
            }
            if (offer.value) offerText += `\nValue: ${offer.value}`;
            if (offer.note) offerText += `\nNote: ${offer.note}`;
            form.formData.offerDetails = offerText.trim();
        } else if (typeof productData.offer === 'string') {
            form.formData.offerDetails = productData.offer;
        } else if (business.campaignStrategy?.recommendedOffer) {
            form.formData.offerDetails = business.campaignStrategy.recommendedOffer;
        }

        // Call to action - try multiple sources
        if (business.goToMarket?.conversionTactics && Array.isArray(business.goToMarket.conversionTactics)) {
            form.formData.callToAction = business.goToMarket.conversionTactics[0] || '';
        }
        // Fallback to campaign strategy
        if (!form.formData.callToAction && business.campaignStrategy?.primaryGoal) {
            const goal = business.campaignStrategy.primaryGoal.toLowerCase();
            if (goal.includes('lead')) form.formData.callToAction = 'Start Free Trial';
            else if (goal.includes('sale')) form.formData.callToAction = 'Get Started Now';
            else form.formData.callToAction = 'Learn More';
        }
        // Fallback based on offer type
        if (!form.formData.callToAction && productData.offer?.type) {
            const offerType = productData.offer.type.toLowerCase();
            if (offerType.includes('subscription') || offerType.includes('saas')) {
                form.formData.callToAction = 'Start Free Trial';
            } else if (offerType.includes('service')) {
                form.formData.callToAction = 'Book a Consultation';
            } else {
                form.formData.callToAction = 'Get Started';
            }
        }

        // Primary channel - try multiple sources
        form.formData.primaryChannel = mapPrimaryChannel(business.goToMarket?.channel);
        // Fallback based on product type
        if (!form.formData.primaryChannel) {
            const apiType = (productData.technicalSpecs?.apiType || '').toLowerCase();
            const desc = (productData.description || '').toLowerCase();
            if (apiType.includes('api') || apiType.includes('saas') || desc.includes('api')) {
                form.formData.primaryChannel = 'landing-page';
            } else if (desc.includes('video') || desc.includes('course')) {
                form.formData.primaryChannel = 'vsl';
            } else {
                form.formData.primaryChannel = 'landing-page'; // Default for B2B products
            }
        }
        console.log('[PRE-FILL] primaryChannel:', form.formData.primaryChannel);

        // Proof assets - infer from product features and competitiveIntelligence
        const proofAssets = [];
        const features = (productData.features || []).join(' ').toLowerCase();
        const productDesc = (productData.description || '').toLowerCase();
        if (features.includes('%') || features.includes('save') || features.includes('reduction') || features.includes('increase')) {
            proofAssets.push('metrics');
        }
        if (features.includes('dashboard') || features.includes('analytics') || features.includes('monitoring')) {
            proofAssets.push('dashboards');
        }
        if (productData.useCases && productData.useCases.length > 0) {
            proofAssets.push('stories');
        }
        if (features.includes('security') || features.includes('compliance') || features.includes('gdpr') || features.includes('hipaa')) {
            proofAssets.push('studies');
        }
        if (productDesc.includes('transform') || productDesc.includes('before') || productDesc.includes('after')) {
            proofAssets.push('before-after');
        }
        // Default: at least metrics and stories if nothing else matched
        if (proofAssets.length === 0) {
            proofAssets.push('metrics', 'stories');
        }
        form.formData.proofAssets = proofAssets;
        console.log('[PRE-FILL] proofAssets:', form.formData.proofAssets);

        // Traffic source - infer from target audience and product type
        const targetAudience = (productData.targetAudience || '').toLowerCase();
        const offerType = (productData.offer?.type || '').toLowerCase();
        if (targetAudience.includes('b2b') || targetAudience.includes('business') || targetAudience.includes('agency') || targetAudience.includes('enterprise')) {
            form.formData.trafficSource = 'cold-linkedin'; // B2B defaults to LinkedIn
        } else if (targetAudience.includes('consumer') || targetAudience.includes('individual')) {
            form.formData.trafficSource = 'cold-ads'; // B2C defaults to ads
        } else if (offerType.includes('subscription') || offerType.includes('saas')) {
            form.formData.trafficSource = 'cold-linkedin'; // SaaS defaults to LinkedIn
        } else {
            form.formData.trafficSource = 'cold-linkedin'; // Default to LinkedIn for professional products
        }
        console.log('[PRE-FILL] trafficSource:', form.formData.trafficSource);

        // Conversion destination - infer from primary channel and offer type
        if (form.formData.primaryChannel === 'vsl') {
            form.formData.conversionDestination = 'vsl';
        } else if (offerType.includes('service') || offerType.includes('consulting')) {
            form.formData.conversionDestination = 'direct-booking';
        } else if (offerType.includes('lead') || productDesc.includes('guide') || productDesc.includes('ebook')) {
            form.formData.conversionDestination = 'lead-magnet';
        } else {
            form.formData.conversionDestination = 'landing-page'; // Default for products
        }
        console.log('[PRE-FILL] conversionDestination:', form.formData.conversionDestination);
    }

    // Log final formData before render
    console.log('[PRE-FILL] FINAL formData:', JSON.stringify(form.formData, null, 2));
    form.render();

    // Store product context
    appState.discoveryContext = {
        productId,
        productData
    };
}

/**
 * Setup welcome card button handlers
 * @param {DiscoveryController} controller - The discovery controller
 * @param {boolean} skipWelcome - If true, skip the welcome card and go straight to form
 */
function setupWelcomeButtons(controller, skipWelcome = false, nexusCampaignName = null) {
    // MP-1.1: Check if form mode is enabled (default: true for new flow)
    const urlParams = new URLSearchParams(window.location.search);
    const useForm = urlParams.get('useForm') !== 'false'; // Default to form

    if (useForm) {
        setupFormMode(skipWelcome, nexusCampaignName);
    } else {
        setupChatMode(controller);
    }
}

/**
 * MP-1.1: Form-based discovery mode
 * @param {boolean} skipWelcome - If true, skip the welcome card and go straight to form
 */
async function setupFormMode(skipWelcome = false, nexusCampaignName = null) {
    const welcomeCard = document.getElementById('welcomeCard');
    const formContainer = document.getElementById('discovery-form-container');
    const chatContainer = document.getElementById('chatContainer');

    // Project buttons - load product data and pre-fill form
    const projectButtons = document.querySelectorAll('.project-btn[data-project]');
    projectButtons.forEach(btn => {
        btn.addEventListener('click', async () => {
            try {
                const productId = btn.getAttribute('data-project');
                const productData = await loadProductData(productId);

                if (productData) {
                    await showDiscoveryFormWithPreFill(productId, productData);
                } else {
                    console.warn(`No product data found for ${productId}, showing empty form`);
                    await showDiscoveryForm(productId);
                }
            } catch (error) {
                console.error('Failed to load product data:', error);
            }
        });
    });

    // Custom product - show modal to get product name, then show form
    const customBtn = document.getElementById('customProductBtn');
    if (customBtn) {
        customBtn.addEventListener('click', async () => {
            let productName;

            if (nexusCampaignName) {
                // NEXUS mode: use project name directly (skip modal)
                productName = nexusCampaignName;
                console.log(`📝 Using NEXUS project name for custom product: ${productName}`);
            } else {
                // Standalone mode: show modal to get product name
                productName = await Modal.customProductModal();
                if (!productName || !productName.trim()) return;
            }

            // Create product with name
            const productId = `custom-${Date.now()}`;
            const productData = {
                id: productId,
                name: productName,
                description: nexusCampaignName ? 'NEXUS Marketing Project' : 'Custom product/service',
                url: '',
                created: new Date().toISOString()
            };

            // Save to localStorage
            localStorage.setItem(`product_${productId}`, JSON.stringify(productData));
            console.log(`Created custom product: ${productName} (${productId})`);

            // Show discovery form with the custom product
            const form = await showDiscoveryForm(productId, productName);

            // Immediately save to server (don't wait 30s for auto-save)
            if (form) {
                try {
                    await form.saveDraft();
                    console.log('📝 Campaign created on server immediately');
                } catch (saveError) {
                    console.error('📝 Immediate save failed:', saveError.message);
                    Modal.toast('Could not save campaign to server — will retry on next auto-save.', 'warning');
                }
            }
        });
    }

    async function showDiscoveryForm(productId, productName = null) {
        // Hide welcome card and chat
        welcomeCard.style.display = 'none';
        chatContainer.style.display = 'none';

        // Show form container
        formContainer.style.display = 'block';

        let draftData = null;

        // Only check for draft if NOT starting a new campaign with a product name
        // When user explicitly creates a new campaign, clear stale draft instead of prompting
        if (!productName) {
            draftData = await DiscoveryForm.checkAndPromptForDraft();
        } else {
            // Clear all stale campaign data from previous sessions
            // This prevents new campaigns from inheriting old product names/slugs
            localStorage.removeItem('discoveryFormDraft');
            localStorage.removeItem('discoveryFormData');
            localStorage.removeItem('pendingCampaignId');
        }

        // Initialize form with draft data if user chose to resume
        const form = new DiscoveryForm(null, draftData);

        // Pre-fill product name if provided (only for fresh start)
        if (productName && !draftData) {
            form.formData.productName = productName;
        }

        // Pass Nexus project ID so saveDraft can link the campaign to Nexus
        form.nexusId = appState.nexusId || null;

        form.render();

        // Store product context for later use
        appState.discoveryContext = {
            productId,
            productName
        };

        return form;
    }

    // Non-admin users: skip welcome card, go directly to the discovery form
    // Emulates standalone "Custom Product" flow:
    // 1. Campaign name comes from server (passed as nexusCampaignName parameter)
    // 2. Show form with name pre-filled (same as standalone after typing name in modal)
    // 3. Immediately save to server via form.saveDraft() (creates campaign without 30s delay)
    if (skipWelcome) {
        console.log('👤 Non-admin user — skipping welcome card, showing form directly');
        const productId = `custom-${Date.now()}`;

        // Show form — name pre-filled if available from server, empty otherwise
        const form = await showDiscoveryForm(productId, nexusCampaignName);

        // Immediately create campaign on server (don't wait 30s for auto-save)
        if (nexusCampaignName && form) {
            try {
                await form.saveDraft();
                console.log('📝 Campaign created on server immediately');
            } catch (saveError) {
                console.error('📝 Immediate save failed:', saveError.message);
                Modal.toast('Could not save campaign to server — will retry on next auto-save.', 'warning');
            }
        }
    }

    // Listen for form completion
    window.addEventListener('discoveryComplete', async (e) => {
        const { formData, campaignId, editMode } = e.detail;

        console.log('✅ Discovery form completed:', formData);

        // Save discovery data to localStorage for framework selector
        localStorage.setItem('discoveryFormData', JSON.stringify(formData));

        let savedCampaignId = campaignId;

        try {
            // Save to server to create campaign.json
            const apiBase = getAPIBase();
            const repository = new DiscoveryRepository(apiBase);
            const result = await repository.saveCampaignDraft(formData, appState.nexusId);
            console.log('💾 Campaign draft saved:', result);

            savedCampaignId = result.campaignId;

            // NEXUS: Send CAMPAIGN_CREATED message
            NexusConfig.sendToParent('CAMPAIGN_CREATED', {
                campaignId: result.campaignId,
                brandName: formData.productName || ''
            });

            Modal.toast('Discovery complete! Proceeding to framework selection...', 'success');

        } catch (error) {
            console.error('Failed to save campaign draft:', error);

            // Non-blocking: warn user but proceed with localStorage data
            Modal.toast('Could not save to server — your data is saved locally.', 'warning');

            // Generate temporary campaignId if none exists
            if (!savedCampaignId) {
                savedCampaignId = `campaign-${Date.now()}`;
            }
        }

        // Store campaignId for framework selector
        localStorage.setItem('pendingCampaignId', savedCampaignId);

        // Redirect to framework selector with campaignId
        NexusConfig.navigateTo('/tools/discovery/web/framework-selector.html', { campaignId: savedCampaignId });
    });
}

/**
 * Chat-based discovery mode (legacy)
 */
function setupChatMode(controller) {
    console.log('💬 Using chat-based discovery (legacy)');

    // Project buttons
    const projectButtons = document.querySelectorAll('.project-btn[data-project]');
    projectButtons.forEach(btn => {
        btn.addEventListener('click', () => {
            const projectId = btn.getAttribute('data-project');
            controller.startDiscovery(projectId);
        });
    });

    // Custom product button
    const customBtn = document.getElementById('customProductBtn');
    if (customBtn) {
        customBtn.addEventListener('click', () => {
            controller.startCustomProduct();
        });
    }

}

// Initialize on DOM ready, once the organisation's time zone is known (its dates are written in it, organisation-time-zone MP-07)
const start = () => window.CampaignTime.ready().then(init);
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
} else {
    start();
}

// Application state is now module-scoped (appState at top of file)
// Access via browser console for debugging if needed
